import { useEffect, useMemo } from "react";
import { useAppDispatch, useSelector } from "../../../../redux-store";
import { loadDelivery } from "../../../../ducks/data/deliveries";
import {
  selectDelivery,
  selectGrossMargin,
} from "../../../../ducks/data/deliveries/selectors";
import { loadProducts } from "../../../../ducks/data/products";
import { selectProducts } from "../../../../ducks/data/products/selectors";
import { draftLinesCleared } from "../../../../ducks/data/draft-invoice-lines";
import { useTranslation } from "react-i18next";
import { useFormContext, useWatch } from "react-hook-form";
import ExpensesNew from "./ExpensesNew";
import Invoices from "./InvoicesNew";
import "./CreateEditShipmentFinancesCard.scss";
import { useL10n } from "../../../../l10n";
import { useFeature } from "../../../../hooks";
import ShipmentDrivingReport from "../shipment-driving-report/ShipmentDrivingReport";
import ShipmentDrivingReportsForSplits from "../shipment-driving-report/ShipmentDrivingReportsForSplits";
import CreateShipmentData from "../CreateEditShipmentData";
import { useDeliveryLiveUpdates } from "./use-delivery-live-updates";

type CreateEditShipmentFinancesCardProps = {
  cargoId: string;
  deliveryId: string;
  clientId?: string;
  clientContact: string | null;
  disableSending: boolean;
  cargoIsSplit: boolean;
  onInvoiceSent: () => void;
  createNew?: boolean;
};

const CreateEditShipmentFinancesAndDrivingReportCard = (
  props: CreateEditShipmentFinancesCardProps
) => {
  const {
    deliveryId,
    cargoId,
    cargoIsSplit,
    clientContact,
    clientId,
    disableSending,
    onInvoiceSent,
    createNew,
  } = props;

  // Watched here rather than in the dialog: a watch() at the dialog root
  // re-rendered the whole dialog on every keystroke in these fields
  // (DISPATCHER-CP / DORA-406).
  const { control } = useFormContext<CreateShipmentData>();
  const [clientContactName, invoiceRef] = useWatch({
    control,
    name: ["clientContact", "invoiceRef"],
  });

  const dispatch = useAppDispatch();

  useDeliveryLiveUpdates(createNew || !deliveryId ? null : deliveryId);

  useEffect(() => {
    if (deliveryId) {
      dispatch(loadDelivery(deliveryId));
      dispatch(loadProducts());
    }
  }, [dispatch, deliveryId]);

  // Create mode: there is no delivery, but we still need products for the line
  // editor, and the in-memory draft must start empty each time the dialog opens.
  useEffect(() => {
    if (createNew) {
      dispatch(loadProducts());
      dispatch(draftLinesCleared());
    }
  }, [dispatch, createNew]);
  const { t } = useTranslation("shipmentModal");
  const l10n = useL10n();
  const delivery = useSelector(selectDelivery(deliveryId));
  const products = useSelector(selectProducts);
  const grossMargin = useSelector(selectGrossMargin(deliveryId));
  const renderExpenses = !useFeature("hide-expenses");
  const splitFeatureEnabled = useFeature("split-cargos-and-routes");
  const canRenderFinanceCard = (createNew || delivery) && products;
  let formattedGrossMargin = "";

  if (grossMargin) {
    formattedGrossMargin = l10n.formatNumber(grossMargin.toDecimalPlaces(2));
    if (!formattedGrossMargin.includes(",")) {
      formattedGrossMargin += ",00";
    } else if (formattedGrossMargin.at(-2) === ",") {
      formattedGrossMargin += "0";
    }
  }
  const shouldShowCargoEventsForSplits = useMemo(
    () => cargoIsSplit && splitFeatureEnabled,
    [cargoIsSplit, splitFeatureEnabled]
  );

  return canRenderFinanceCard ? (
    <div className="shipment-dialog-finances__wrapper">
      <div className="shipment-dialog-finances__subtitle">
        {t("cards.finances.incomeTitle")}
      </div>
      <Invoices
        cargoId={cargoId}
        deliveryId={deliveryId}
        clientId={clientId}
        clientContact={clientContact}
        clientContactName={clientContactName}
        invoiceRef={invoiceRef}
        onInvoiceSent={onInvoiceSent}
        disableSending={disableSending}
        createNew={createNew}
      />
      {!disableSending && cargoId && (
        <div className="mt-12 mb-6">
          <ShipmentDrivingReport
            cargoId={cargoId}
            clientId={clientId || null}
          />
          {shouldShowCargoEventsForSplits && (
            <ShipmentDrivingReportsForSplits cargoId={cargoId} />
          )}
        </div>
      )}
      {renderExpenses && !createNew && (
        <>
          <div className="shipment-dialog-finances__subtitle">
            {t("cards.finances.expensesTitle")}
          </div>
          <ExpensesNew deliveryId={deliveryId} />
          <br />
          {grossMargin && (
            <div className="shipment-dialog-finances__gross-margin-wrapper">
              <div className="shipment-dialog-finances__gross-margin">
                <div>{t("cards.finances.grossMargin")}</div>
                <div>{formattedGrossMargin}</div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  ) : null;
};

export default CreateEditShipmentFinancesAndDrivingReportCard;
